export interface TwitchConnect
{
    profileID: `twitch|${string}`;
    access_token: string;
    refresh_token: string;
    expires_at: Date;
}

export class DBAccount
{
    public declare id: number;

    public declare connection: string;

    public declare email: string;

    public declare password: string;

    public declare is_signup: boolean;

    public declare tenant: string;

    public declare _v: number;

    public declare username: string;


    /**
     * The url of the user's photo.
     */
    public declare photo_path: string;

    public declare verified: boolean;

    public declare bio: string | null;

    public declare settings: SettingsStructure;

    public declare connection_tokens: {
        access_token: string;
        refresh_token: string;
        expires_at: Date;
    };

    public declare connections: (string|TwitchConnect)[] | null;
}

/**
 * Represents a daily headline.
 * This is used to store the daily headline that is displayed to users.
 */
export interface DailyHeadline
{
    /**
     * The ID of the daily headline.
     */
    id: number;

    /**
     * The headline text.
     */
    headline: string;

    /**
     * The date and time when the headline was created.
     */
    created_at: Date;

    /**
     * The ID of the profile who created the headline.
     * This is used to link the headline to the profile who created it.
     *
     * This can be null if the headline was created by an the realize monthly headline.
     */
    created_by: number|null;

    /**
     * Whether or not the headline is closed.
     * A closed headline means that no more submissions can be made for it.
     * This is typically set to true after the daily headline has been announced OR
     * the profile who created the headline has closed it so they can deliberate on a winner.
     */
    closed: boolean;

    /**
     * The ID of the winning post.
     * This is the post that was selected as the winner for the daily headline.
     * If this is null, then no winner has been selected yet.
     */
    winning_post_id: number | null;
}

/**
 * Represents a submission for a daily headline.
 * This is used to store the user's suggestion for a daily headline.
 * The submission is linked to a specific daily headline.
 */
export interface DailyHeadlineSubmission
{
    /**
     * The ID of the submission.
     */
    id: number;

    /**
     * The ID of the daily headline.
     */
    headline_id: number;

    /**
     * The headline suggestion if this submission is the winner.
     */
    headline_suggestion: string;

    /**
     * The photo associated with the submission.
     */
    photo_path: string;

    /**
     * The date and time when the submission was created.
     */
    created_at: Date;

    /**
     * The ID of the human who made the submission.
     */
    profile_id: number;

    /**
     * The description text for the submission.
     */
    description_text: string;
}

/**
 * Represents a human (Unique Device).
 * This is used to store the information about a human who is playing the game.
 */
export interface Human
{
    /**
     * The ID of the human.
     */
    id: number;

    roundWinCount: number;
    roundLoseCount: number;

    /**
     * The unique identifier for the human.
     */
    currentClient: string;
    currentRooms: string[];
    gameHistory: string[];

    /**
     * The date and time when the human was created.
     */
    createdAt: Date;
    updateAt: Date;

    displayName: string | null;
    displayPhoto: string | null;
}


export interface DailyHeadlineComment
{
    id: number;
    comment: string;
    profile_id: number;
    submission_id: number;
    created_at: Date;
}

export interface DailyHeadlineSubmissionLike
{
    profile_id: number;
    submission_id: number;
    created_at: Date;
}

export interface DailyHeadlineCommentLike
{
    profile_id: number;
    comment_id: number;
    created_at: Date;
}

export interface PreviousHeadlineSubmission
{
    headline_id: number,
    headline: string,
    created_at: Date,
    submission_id: number,
    submission_content: string;
    submission_point_count: number,
    profile_id: number,
    username: string,
    profile_photo: string,
    is_winner: boolean,
}

export type DailyHeadlineHasSubmitted = false
| {
    photo_path: string;
    description: string;
    victory_headline: string;
    totalLikes: number;
    totalComments: number;
}
| null;


export interface SearchProfile
{
    id: number;
    username: string;
    photo_path: string | null;
    verified: boolean;
    bio: string | null;
    isTwitch: boolean;
}

export interface ProfileHeadline
{
    id: number;
    headline: string;
    created_by: number;
    created_at: Date;
    submissions: number;
    hasSubmitted: boolean;
    closed: boolean;
}

export type NotificationEntityType = "left_comment" | "liked_comment" | "followed_profile" |"unknown";

export interface NotificationObject
{
    id: number;
    entity_type_id: NotificationEntityType;
    entity_id: number;
    notification_object: number;
    notifier_id: number;
    notifier_username: string | null;
    notifier_photo: string | null;
    actor_id: number | null;
    actor_username: string | null;
    actor_photo: string | null;
    read: boolean;
    entity?: {
        id: number;
        type: "left_comment"
        content?: DailyHeadlineSubmission; // Optional, can be used for comments or likes
    } | {
        id: number;
        type: "liked_comment",
        content: string; // Optional, can be used for comments or likes
    } | {
        id: number;
        type: "unknown",
        content?: string; // Optional, can be used for comments or likes
    } | {
        id: number;
        type: "followed_profile",
        content?: string; // Optional, can be used for comments or likes
    }
    created_on: Date;
}

export interface SettingsStructure
{
    searchDiscovery: boolean;
    privateAccount: boolean;
    followingOnly: boolean;
    followersOnly: boolean;
}

export interface Rules
{
    pointsToWin: number;
    roundLength: number; // time in MS
    visibility: "public" | "redacted" | "private";
    editor: "static" | "random";
}

export const enum Phase
{
    LOBBY = 1,

    HEADLINE_CREATION = 2,

    PHOTO_SUBMISSION = 3,

    VOTING = 4,

    CLOSED = 5,
}

export interface DBRoom
{
    _v: 1,
    roomID: string;
    issue: number;
    roundCount: number;
    headlines: Text[];
    rules: Rules;
    state:
    {
        clockEnd: number;
        phase: Phase;
    };
    staleCount: number;
    host: string;
    editor: string;
    editorOrder: string[];
    players: string[];
}

export interface LiveGame
{
    id: number;
    roomID: string;
    hostName: string;
    currentRound: number;
    timeLeft: number; // in milliseconds
    rules: Rules;
    phase: Phase;
    players: { human_id: number; name: string; avatar: string }[];
}

export interface DBSpectator
{
    spectator_id: number;
    room_id: number;
    profile_id: number | null;
    ip_address: string;
    created_at: Date;
    device_id: string;
}

export interface ProcessedSpectator
{
    spectator_id: number;
    room_id: number;
    profile: DBAccount | null;
    ip_address: string;
    created_at: Date;
}

/**
 * Represents the privacy state of a profile.
 * This is used to determine who can see the profile and its content.
 */
export enum PrivacyState
{

    /**
     * Represents a profile that is visible to everyone.
     */
    PUBLIC = "public",

    /**
     * Represents a profile that is only visible to users that are being followed by the profile.
     */
    FOLLOWING = "following",

    /**
     * Represents a profile that is only visible to users that are following the profile.
     */
    FOLLOWERS = "followers",

    /**
     * Represents a profile that is only visible to both followers that are being followed by the profile.
     */
    BOTH = "both",
}
